// src/pages/TestOrders/sections/RequestedTestsAndPanelsSection.jsx
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Input,
    SimpleGrid,
    Text,
    VStack
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { getPanels, getTests, resolveIcd10ByCpts } from '../../../../api';
import AbnPdf from '../AbnPdf';
import RequisitionFieldRenderer from '../RequisitionFieldRenderer';
import PanelSelect from './PanelSelect';

const RequestedTestsAndPanelsSection = ({ formData, setFormData }) => {
    const [tests, setTests] = useState([]);
    const [panels, setPanels] = useState([]);
    const [selectedTests, setSelectedTests] = useState([]);
    const [selectedPanels, setSelectedPanels] = useState(null);
    const [method, setMethod] = useState(null);

    // fetch tests + panels once
    useEffect(() => {
        const fetchData = async () => {
            const testRes = await getTests();
            if (testRes.success) {
                setTests(
                    (testRes.data?.items || []).map((t) => ({
                        value: t._id,
                        label: t.name,
                        ...t,
                    }))
                );
            }

            const panelRes = await getPanels();
            if (panelRes.success) {
                setPanels(
                    (panelRes.data || []).map((p) => ({
                        value: p._id,
                        label: p.name,
                        ...p,
                    }))
                );
            }
        };
        fetchData();
    }, []);

    // ensure at least 1 test row on load
    useEffect(() => {
        if (selectedTests.length === 0) {
            addTest();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addTest = () =>
        setSelectedTests([
            ...selectedTests,
            { test: null, icdOptions: [], icdSelected: null, requisitionValues: {} },
        ]);

    const updateTest = async (idx, opt) => {
        const updated = [...selectedTests];
        updated[idx] = {
            ...updated[idx],
            test: opt,
            icdOptions: [],
            icdSelected: null,
            requisitionValues: {},
        };
        setSelectedTests(updated);

        if (opt?.cpts?.length > 0) {
            try {
                const icdRes = await resolveIcd10ByCpts(opt.cpts);
                if (icdRes.success) {
                    updated[idx].icdOptions = (icdRes.data?.icd10 || []).map((c) => ({
                        value: c.code,
                        label: c.description ? `${c.code} - ${c.description}` : c.code,
                    }));
                    setSelectedTests([...updated]);
                }
            } catch (e) {
                console.error('Failed to fetch ICD-10:', e);
            }
        }
    };

    const updateIcd = (idx, code) => {
        const updated = [...selectedTests];
        updated[idx].icdSelected = code;
        setSelectedTests(updated);
    };

    const updateRequisitionField = (testIdx, fieldKey, value) => {
        const updated = [...selectedTests];
        updated[testIdx].requisitionValues[fieldKey] = value;
        setSelectedTests(updated);
    };

    const removeTest = (idx) => {
        if (selectedTests.length > 1) {
            setSelectedTests(selectedTests.filter((_, i) => i !== idx));
        }
    };

    // detect custom ICD usage
    const customIcdTests = selectedTests.filter(
        (sel) =>
            sel.icdSelected &&
            !sel.icdOptions.find((opt) => opt.value === sel.icdSelected.value)
    );



    return (
        <>

            <SimpleGrid templateColumns="repeat(2, 1fr)" gap={6}>
                <Box borderWidth="1px" borderRadius="md" p={4}>
                    <Heading size="md" mb={4}>
                        Individual Tests
                    </Heading>
                    {/* Individual Tests */}
                    <Box p={4} shadow="sm" mb={6}>


                        {selectedTests.map((sel, idx) => (
                            <Box
                                key={idx}
                                p={3}
                                borderWidth="1px"
                                rounded="md"
                                mb={3}
                                position="relative"
                            >
                                <FormControl mb={3}>
                                    <FormLabel>Test #{idx + 1}</FormLabel>
                                    <Select
                                        options={tests}
                                        value={sel.test}
                                        onChange={(opt) => updateTest(idx, opt)}
                                        placeholder="Select Test"
                                    />
                                </FormControl>

                                <FormControl mb={3}>
                                    <FormLabel>ICD-10 Code</FormLabel>
                                    <CreatableSelect
                                        isClearable
                                        options={sel.icdOptions}
                                        value={sel.icdSelected}
                                        onChange={(code) => updateIcd(idx, code)}
                                        placeholder="Select or type ICD-10 code"
                                    />
                                </FormControl>

                                {sel.test?.requisitionSchema?.fields?.length > 0 && (
                                    <Box mt={3}>
                                        <Heading size="xs" mb={2}>
                                            {sel.test.requisitionSchema.title || 'Requisition'}
                                        </Heading>
                                        {sel.test.requisitionSchema.fields.map((f) => (
                                            <RequisitionFieldRenderer
                                                key={f.key}
                                                field={f}
                                                value={sel.requisitionValues[f.key]}
                                                onChange={(val) => updateRequisitionField(idx, f.key, val)}
                                            />
                                        ))}
                                    </Box>
                                )}

                                <Box>
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        colorScheme="red"
                                        onClick={() => removeTest(idx)}
                                        isDisabled={selectedTests.length === 1}
                                    >
                                        Remove This Test
                                    </Button>
                                </Box>
                            </Box>
                        ))}

                        <Button size="xs" colorScheme="blue" onClick={addTest}>
                            Add More Tests
                        </Button>
                    </Box>


                </Box>
                <Box borderWidth="1px" borderRadius="md" p={4}>
                    <Heading size="sm" mb={2}>Panels & Profiles</Heading>

                    {/* Panels & Profiles */}
                    <Box p={4} shadow="sm">
                        <FormControl>
                            <FormLabel>Select Panels</FormLabel>
                            {/* <Select
                                options={panels}
                                value={selectedPanels}
                                onChange={(opt) => setSelectedPanels(opt)}
                                placeholder="Select panel"
                                isClearable
                            /> */}

                            <PanelSelect panels={panels} />

                        </FormControl>




                    </Box>


                    {selectedPanels && (
                        <Box mt={6} p={4} borderWidth="1px" borderRadius="md">
                            <Heading size="md" mb={2}>
                                {selectedPanels.name}
                            </Heading>
                            <Text mb={4}>{selectedPanels.description}</Text>

                            <Heading size="sm" mb={2}>
                                Tests
                            </Heading>
                            <VStack align="stretch" spacing={4}>
                                {selectedPanels.tests?.map((test) => (
                                    <Box
                                        key={test._id}
                                        p={3}
                                        borderWidth="1px"
                                        borderRadius="md"
                                    >
                                        <Heading size="sm">{test.name}</Heading>
                                        <Text fontSize="sm" color="gray.600">
                                            Code: {test.code} | Number: {test.number} | Short:{" "}
                                            {test.shortCode}
                                        </Text>
                                        <Text mt={1}>{test.description}</Text>
                                        <Text mt={1}>
                                            CPTs: {test.cpts.join(", ")}
                                        </Text>
                                        <Text mt={1}>
                                            Specimen: {test.defaultSpecimen.type}
                                        </Text>
                                        {test.requisitionSchema && (
                                            <Box mt={2}>
                                                <Text fontWeight="bold">Requisition:</Text>
                                                {test.requisitionSchema.fields.map((f) => (
                                                    <Text key={f.key} fontSize="sm">
                                                        {f.label} ({f.type})
                                                    </Text>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </VStack>
                            <Divider mt={4} />
                            <Text fontSize="xs" mt={2} color="gray.500">
                                Created: {new Date(selectedPanels.createdAt).toLocaleString()}
                            </Text>
                        </Box>
                    )}

                </Box>
            </SimpleGrid>







            {/* ABN Warning */}
            {customIcdTests.length > 0 && (
                <Alert status="warning" mb={4} justifyContent="center" textAlign="center">
                    <VStack spacing={4}>
                        <Heading size="sm">
                            ABN is required, Please download and print ABN and have the patient
                            sign it.
                        </Heading>

                        {/* <Button colorScheme="green" size="sm" onClick={downloadAbnPdf}>
                            Download ABN
                        </Button> */}

                        <Heading size="xs" mt={2}>
                            OR send this ABN to patient phone or email
                        </Heading>
                        <HStack spacing={3}>
                            <Button
                                size="sm"
                                colorScheme={method === 'email' ? 'blue' : 'gray'}
                                onClick={() => setMethod('email')}
                            >
                                Email
                            </Button>
                            <Button
                                size="sm"
                                colorScheme={method === 'sms' ? 'purple' : 'gray'}
                                onClick={() => setMethod('sms')}
                            >
                                SMS
                            </Button>
                        </HStack>

                        {method === 'email' && (
                            <Input
                                placeholder="Enter email address"
                                size="md"
                                bg="white"
                                w="250px"
                            />
                        )}
                        {method === 'sms' && (
                            <Input
                                placeholder="Enter mobile number"
                                size="md"
                                bg="white"
                                w="250px"
                            />
                        )}
                    </VStack>
                </Alert>
            )}

            {/* Hidden ABN PDF template */}
            {customIcdTests.length > 0 && (
                <AbnPdf customIcdTests={customIcdTests} />
            )}
        </>
    );
};

export default RequestedTestsAndPanelsSection;
